export type TicketKind = "TICKET" | "ADD_ON" | "SPONSORSHIP";

export type TicketOption = { id: string; name: string; kind: TicketKind };

// Display order + headings for ticket kinds, matching TicketsStep.
export const TICKET_KIND_ORDER: { kind: TicketKind; label: string }[] = [
    {kind: "TICKET", label: "Ticket"},
    {kind: "ADD_ON", label: "Add-on"},
    {kind: "SPONSORSHIP", label: "Sponsorship"},
];

export function groupTicketsByKind(tickets: TicketOption[]) {
    return TICKET_KIND_ORDER
        .map(({kind, label}) => ({
            kind,
            label,
            items: tickets.filter((t) => t.kind === kind),
        }))
        .filter((g) => g.items.length > 0);
}

export function TicketScopePicker({
                                      label,
                                      ticketTypeIds,
                                      tickets,
                                      onChange,
                                  }: {
    label: string;
    ticketTypeIds: string[];
    tickets: TicketOption[];
    onChange: (next: string[]) => void;
}) {
    const allTickets = ticketTypeIds.length === 0;
    return (
        <div className="mt-2 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="text-xs font-semibold text-gray-900">Applies to all tickets</div>
                    <div className="text-xs text-gray-600">
                        Turn off to {label.toLowerCase()} only for specific ticket types.
                    </div>
                </div>
                <input
                    type="checkbox"
                    checked={allTickets}
                    onChange={(e) => onChange(e.target.checked ? [] : tickets.map((t) => t.id))}
                    className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                />
            </div>

            {!allTickets ? (
                <div className="mt-3 space-y-3">
                    {tickets.length === 0 ? (
                        <div className="text-xs text-gray-500">No tickets configured yet.</div>
                    ) : (
                        groupTicketsByKind(tickets).map((group) => {
                            const groupIds = group.items.map((t) => t.id);
                            const allSelected = groupIds.every((id) => ticketTypeIds.includes(id));
                            const noneSelected = groupIds.every((id) => !ticketTypeIds.includes(id));
                            return (
                            <div key={group.kind} className="space-y-2">
                                <div className="flex items-center justify-between gap-2">
                                    <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                                        {group.label}
                                    </div>
                                    <div className="flex items-center gap-2 text-[11px] font-medium">
                                        <button
                                            type="button"
                                            disabled={allSelected}
                                            onClick={() =>
                                                onChange([
                                                    ...ticketTypeIds,
                                                    ...groupIds.filter((id) => !ticketTypeIds.includes(id)),
                                                ])
                                            }
                                            className="text-orange-700 hover:text-orange-800 disabled:text-gray-300 disabled:cursor-default"
                                        >
                                            Select all
                                        </button>
                                        <span className="text-gray-300">·</span>
                                        <button
                                            type="button"
                                            disabled={noneSelected}
                                            onClick={() =>
                                                onChange(ticketTypeIds.filter((id) => !groupIds.includes(id)))
                                            }
                                            className="text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:cursor-default"
                                        >
                                            Deselect all
                                        </button>
                                    </div>
                                </div>
                                {group.items.map((t) => {
                                    const checked = ticketTypeIds.includes(t.id);
                                    return (
                                        <label key={t.id} className="flex items-center gap-3">
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    const next = e.target.checked
                                                        ? [...ticketTypeIds, t.id]
                                                        : ticketTypeIds.filter((id) => id !== t.id);
                                                    onChange(next);
                                                }}
                                                className="size-4 rounded border-gray-300 text-orange-700 focus:ring-orange-600"
                                            />
                                            <span className="text-sm text-gray-900">{t.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            );
                        })
                    )}
                </div>
            ) : null}
        </div>
    );
}
